import { notyf } from '@modules';

/**
 * <dynamic-form> — Emarsys-app submit mode for the `dynamic-form` section.
 *
 * Same pattern as emarsys-preferences.js / the withdrawal page: collect the
 * form fields and POST them as JSON to the TCG Emarsys Gadget app, which reads
 * the SHOP-SPECIFIC event id from the shop record (never hardcoded in the theme)
 * and triggers the DOI event. No reCAPTCHA — the app owns trust (NGE-7762 / NGE-8351).
 *
 * Data attributes (set by dynamic-form.liquid):
 *   data-env         — shop.metafields.shop.env (production | stage | development)
 *   data-endpoint    — app route path, e.g. /influencer (contract: tcg-emarsys docs/influencer.md)
 *   data-shop-id     — shop.metafields.shop.id (→ payload shopId)
 *   data-success-message / data-error-message — optional UI copy
 */
class DynamicForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.successEl = this.querySelector('[data-dynamic-form-success]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  buildUrl() {
    const env = this.dataset.env || 'production';
    const envSlug = env === 'production' ? '' : `--${env}`;
    // Contract (tcg-emarsys docs/influencer.md): POST {shopId, fields} to
    // https://tcg-emarsys{--env}.gadget.app<endpoint>. shopId travels in the body.
    return `https://tcg-emarsys${envSlug}.gadget.app${this.dataset.endpoint || ''}`;
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    if (!this.dataset.endpoint) {
      notyf.error(this.dataset.errorMessage || 'This form is not configured yet.');
      return;
    }

    const fields = {};
    for (const [key, value] of new FormData(this.form).entries()) fields[key] = value;
    const payload = { shopId: this.dataset.shopId || '', fields };

    this.toggleLoading(true);
    try {
      const response = await fetch(this.buildUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success !== true) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      this.showSuccess();
    } catch {
      notyf.error(this.dataset.errorMessage || 'Something went wrong — please try again.');
    } finally {
      this.toggleLoading(false);
    }
  }

  showSuccess() {
    if (!this.successEl) {
      notyf.success(this.dataset.successMessage || 'Thanks — we received your submission.');
      return;
    }
    this.form.hidden = true;
    this.successEl.hidden = false;
    this.successEl.setAttribute('tabindex', '-1');
    this.successEl.focus();
  }

  toggleLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle('loading', isLoading);
  }
}

customElements.define('dynamic-form', DynamicForm);
